import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export type ChatMsg = { id: string; role: 'me' | 'ai' | 'thinking'; content: string };

export interface ChatPaneProps {
  title: string;
  scopeLabel: string;
  suggestions: string[];
  initialMessage: string;
  /** Stream a reply token-by-token (or chunk-by-chunk) — wired to real LLM in prod. */
  send: (question: string, history: ChatMsg[], onToken: (t: string) => void) => Promise<void>;
}

export function ChatPane({ title, scopeLabel, suggestions, initialMessage, send }: ChatPaneProps) {
  const [msgs, setMsgs] = useState<ChatMsg[]>([
    { id: 'init', role: 'ai', content: initialMessage },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scroll = useRef<HTMLDivElement | null>(null);

  // `useState` only reads its initializer once, so a greeting built from data
  // that is still loading froze at its empty form — the Inventory pane sat on
  // "0 users" for good once the user list moved to a live endpoint that takes
  // 13-20 s. Re-seed while the greeting is still the only message; once the
  // conversation has started, leave the transcript alone.
  useEffect(() => {
    setMsgs((m) =>
      m.length === 1 && m[0]?.id === 'init'
        ? [{ id: 'init', role: 'ai', content: initialMessage }]
        : m,
    );
  }, [initialMessage]);

  useEffect(() => {
    scroll.current?.scrollTo({ top: scroll.current.scrollHeight, behavior: 'smooth' });
  }, [msgs]);

  const submit = async (q: string) => {
    if (!q || busy) return;
    const userMsg: ChatMsg = { id: `u-${Date.now()}`, role: 'me', content: q };
    const aiId = `ai-${Date.now()}`;
    setMsgs((prev) => [...prev, userMsg, { id: aiId, role: 'thinking', content: '' }]);
    setInput('');
    setBusy(true);
    let accumulated = '';
    try {
      await send(q, msgs, (token) => {
        accumulated += token;
        setMsgs((prev) =>
          prev.map((m) => (m.id === aiId ? { ...m, role: 'ai', content: accumulated } : m)),
        );
      });
    } catch {
      setMsgs((prev) =>
        prev.map((m) =>
          m.id === aiId ? { ...m, role: 'ai', content: 'Connection error. Try again.' } : m,
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="flex items-center gap-2 px-3 py-1.5 border-b border-(--color-border) shrink-0 bg-(--color-bg)">
        <span className="text-[0.769em] font-bold">{title}</span>
        <span className="text-[0.538em] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-(--color-blue) text-(--color-on-accent)">
          AI
        </span>
        <span className="ml-auto text-[0.615em] font-mono text-(--color-text3)">{scopeLabel}</span>
      </header>
      <div ref={scroll} className="flex-1 overflow-y-auto p-2 space-y-2">
        {msgs.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
      </div>
      {/* Horizontal scroll instead of wrap — wrapping ate vertical space and
       *  pushed the input form below the docked R-panel's visible area
       *  on shorter viewports. */}
      <div className="px-2 pb-1 flex gap-1 flex-nowrap overflow-x-auto shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => submit(s)}
            disabled={busy}
            className="shrink-0 px-2 h-6 rounded-full text-[0.615em] font-semibold border border-(--color-border) bg-(--color-surface) hover:border-(--color-green) disabled:opacity-50 whitespace-nowrap"
          >
            {s}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        className="flex gap-1 p-2 border-t border-(--color-border) shrink-0 bg-(--color-surface)"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${scopeLabel}…`}
          disabled={busy}
          className="flex-1 h-8 px-2 rounded-md border border-(--color-border) bg-(--color-bg) text-[0.846em] outline-none focus:border-(--color-green)"
        />
        <Button type="submit" size="icon" disabled={busy || !input}>
          <Send size={13} />
        </Button>
      </form>
    </div>
  );
}

function Bubble({ m }: { m: ChatMsg }) {
  if (m.role === 'me') {
    return (
      <div className="ml-8 rounded-lg bg-(--color-green) text-(--color-on-accent) px-2.5 py-1.5 text-[0.846em] self-end">
        {m.content}
      </div>
    );
  }
  if (m.role === 'thinking') {
    return (
      <div className="mr-8 rounded-lg bg-(--color-bg) border border-(--color-border) px-2.5 py-1.5 text-[0.846em] text-(--color-text3) flex items-center gap-1">
        <span className="size-1.5 rounded-full bg-(--color-text3) animate-bounce" />
        <span
          className="size-1.5 rounded-full bg-(--color-text3) animate-bounce"
          style={{ animationDelay: '0.1s' }}
        />
        <span
          className="size-1.5 rounded-full bg-(--color-text3) animate-bounce"
          style={{ animationDelay: '0.2s' }}
        />
      </div>
    );
  }
  return (
    <div className="mr-8 rounded-lg bg-(--color-bg) border border-(--color-border) px-2.5 py-1.5 text-[0.846em]">
      <div className="text-[0.538em] font-bold uppercase tracking-wider text-(--color-text3) mb-0.5">
        FLYsafe AI
      </div>
      <div className={cn('leading-relaxed whitespace-pre-wrap')}>{m.content}</div>
    </div>
  );
}
